import React from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import CommunicationButtons from "./CommunicationButtons";
import UserActionsDropdown from "./UserActionsDropdown";
import configCountries from "../../../Config/countries.json";

const UserRow = ({ item, t, onAction }) => {
    if (!item) return null;

    // Resolve Country object
    const countryObj = item.Country
        ? configCountries.countries.find(
              (c) => c.value.toLowerCase() === item.Country.toLowerCase()
          )
        : null;

    const userRole = item.UserType ? item.UserType.Name : "";
    const userTypeId = item.UserType?.Id;
    const isTeacher = userTypeId === 2 || userTypeId === 4;
    const isStudent = userTypeId === 5;
    const isAdmin = userTypeId === 1;

    // Badge class
    let roleBadgeClass = "badge-secondary";
    if (isAdmin) roleBadgeClass = "badge-danger";
    else if (isTeacher) roleBadgeClass = "badge-info";
    else if (isStudent) roleBadgeClass = "badge-primary";

    return (
        <tr className="user-row align-middle text-right">
            {/* User Info Column */}
            <td>
                <div className="d-flex align-items-center">
                    <div 
                        className="user-avatar-wrapper"
                        title={item.ActivateUntilDate && item.ActivateUntilDate !== "0" ? `${t("Pages.Profile.ActivateUntilDate.title")}: ${item.ActivateUntilDate}` : undefined}
                    >
                        <LazyLoadImage
                            src={item.Photo || "/assets/images/logo/130p150.png"}
                            alt={item.Name}
                            className={`user-avatar-img ${item.IsActive ? "border-active" : "border-inactive"}`}
                            title={item.IsActive ? t("Pages.UsersControl.Active") : t("Pages.UsersControl.NotActive")}
                        />
                        {item.ActivateUntilDate && item.ActivateUntilDate !== "0" && (
                            <span className="user-crown-badge">
                                <i className="fa-solid fa-crown"></i>
                            </span>
                        )}
                        {!item.IsActive && (
                            <span
                                className="position-absolute d-flex align-items-center justify-content-center bg-danger text-white rounded-circle shadow-sm user-avatar-badge-top"
                                title={t("Pages.UsersControl.NotActive")}
                            >
                                <i className="fas fa-lock"></i>
                            </span>
                        )}
                        {item.IsLocked && (
                            <span
                                className="position-absolute d-flex align-items-center justify-content-center bg-dark text-white rounded-circle shadow-sm user-avatar-badge-bottom"
                                title={t("Pages.UsersControl.IsLocked")}
                            >
                                <i className="fas fa-user-lock"></i>
                            </span>
                        )}
                    </div>
                    <div className="d-flex flex-column text-right">
                        <div className="d-flex align-items-center justify-content-start">
                            <button
                                type="button"
                                className="btn btn-link p-0 text-right font-weight-bold text-dark text-decoration-none user-name-btn"
                                onClick={() => onAction(item.IsDeleted ? "restore" : "edit", item)}
                            >
                                {item.Name}
                            </button>
                            {item.Sex && (
                                <i 
                                    className={`fas fa-${item.Sex === "female" ? "venus text-danger" : "mars text-primary"} mr-2`} 
                                    title={t('Pages.Register.Sex.' + item.Sex)}
                                    style={{ fontSize: '0.9rem' }}
                                ></i>
                            )}
                        </div>
                        <span className="text-muted small mt-1">@{item.Username}</span>
                    </div>
                </div>
            </td>

            {/* Contact Details Column */}
            <td>
                <div className="d-flex flex-column text-right">
                    {item.Email ? (
                        <a href={`mailto:${item.Email}`} className="contact-link">
                            <i className="fal fa-envelope-open-text ml-1"></i>
                            <span>{item.Email}</span>
                        </a>
                    ) : (
                        <span className="text-muted small">-</span>
                    )}
                    {item.Phone ? (
                        <div className="phone-row-wrapper mt-1" dir="ltr">
                            {countryObj && (
                                <LazyLoadImage
                                    src={process.env.PUBLIC_URL + countryObj.icon}
                                    alt={countryObj.label}
                                    className="user-country-flag mr-1 ml-0"
                                    style={{ margin: 0, width: '24px', height: '16px', boxShadow: 'none' }}
                                    title={`${countryObj.label}${item.Address ? ' - ' + item.Address : ''}`}
                                />
                            )}
                            <a href={`tel:${item.Phone}`} className="contact-link mb-0" style={{ whiteSpace: 'nowrap' }}>
                                <i className="fal fa-phone-alt mr-1"></i>
                                <span>{item.Phone}</span>
                            </a>
                            <CommunicationButtons item={item} t={t} compact={true} />
                        </div>
                    ) : (
                        <span className="text-muted small">-</span>
                    )}
                </div>
            </td>

            {/* User Type Badge & School Column */}
            <td className="text-center">
                <div className="d-flex flex-column align-items-center justify-content-center gap-1">
                    <span className={`badge ${roleBadgeClass} user-role-badge`}>
                        {userRole}
                    </span>
                    {item.School && userTypeId !== 1 && userTypeId !== 6 && (
                        <span className="school-info-text text-muted small mt-1" style={{ whiteSpace: 'nowrap' }}>
                            <i className="fal fa-school ml-1"></i>
                            {item.School.Name}
                        </span>
                    )}
                </div>
            </td>

            {/* Actions Dropdown */}
            <td className="text-center align-middle">
                <div className="d-flex align-items-center justify-content-center">
                    <UserActionsDropdown item={item} t={t} onAction={onAction} />
                </div>
            </td>
        </tr>
    );
};

export default UserRow;
